import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  InputAdornment,
  Chip,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Search as SearchIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Redo as ResendIcon,
  Image as ImageIcon,
  Upload as UploadIcon,
  FolderOpen as FolderIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

const SendNotification = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://printformee.com/api";

  const [formData, setFormData] = useState({
    title: "",
    message: "",
    imageUrl: "",
  });

  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Load notifications on component mount
  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      // For now, we'll load recent notifications (this would need a get all notifications endpoint for admin)
      const response = await axios.get(`${API_BASE_URL}/notifications`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        timeout: 5000, // Add timeout
      });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error("Error loading notifications:", error);
      // If endpoint doesn't work or returns 500, keep empty array and show user-friendly message
      if (error.response?.status === 500) {
        console.warn("Notifications endpoint is not available");
      }
      setNotifications([]); // Ensure notifications is always an array
    }
  };

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // For now, just store the file. In production, upload to cloud storage
      setFormData({
        ...formData,
        imageUrl: file.name, // Placeholder - would be actual URL after upload
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.message) {
      setAlert({
        open: true,
        message: "Please fill in all required fields",
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/notifications/broadcast`,
        {
          title: formData.title,
          message: formData.message,
          type: "promotion",
          imageUrl: formData.imageUrl,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        },
      );

      setAlert({
        open: true,
        message: `Notification sent to ${response.data.sentTo} users successfully!`,
        severity: "success",
      });

      // Add to local list for immediate display
      const newNotification = {
        _id: Date.now(),
        title: formData.title,
        message: formData.message,
        imageUrl: formData.imageUrl,
        type: "promotion",
        isRead: false,
        createdAt: new Date(),
        sentTo: response.data.sentTo,
      };

      setNotifications([newNotification, ...notifications]);
      handleReset();
    } catch (error) {
      console.error("Error sending notification:", error);
      setAlert({
        open: true,
        message: "Failed to send notification. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      title: "",
      message: "",
      imageUrl: "",
    });
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/notifications/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });

      setNotifications(
        notifications.filter((notification) => notification._id !== id),
      );
      setAlert({
        open: true,
        message: "Notification deleted successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting notification:", error);
      setAlert({
        open: true,
        message: "Failed to delete notification",
        severity: "error",
      });
    }
  };

  const filteredNotifications = notifications.filter((notification) =>
    notification.title.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography
        variant={isMobile ? "h5" : "h4"}
        sx={{ mb: 3, fontWeight: 600 }}
      >
        Send Notification
      </Typography>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>

      {/* Notification Creation Form */}
      <Card sx={{ mb: { xs: 3, md: 4 } }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            sx={{
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: "1.1rem", sm: "1.25rem" },
            }}
          >
            Create New Notification
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Title"
                  value={formData.title}
                  onChange={handleInputChange("title")}
                  placeholder="Enter notification title"
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={isMobile ? 3 : 4}
                  label="Message"
                  value={formData.message}
                  onChange={handleInputChange("message")}
                  placeholder="Enter notification message"
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    border: "2px dashed #ccc",
                    borderRadius: 2,
                    p: 3,
                    textAlign: "center",
                  }}
                >
                  <input
                    type="file"
                    id="image-upload"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: "none" }}
                  />
                  <label htmlFor="image-upload" style={{ cursor: "pointer" }}>
                    {formData.imageUrl ? (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ mt: 1, color: "#666" }}
                        >
                          Image selected: {formData.imageUrl}
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        <UploadIcon
                          sx={{ fontSize: 48, color: "#ccc", mb: 2 }}
                        />
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          Click to upload image (optional)
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#999" }}>
                          Ratio 1:1
                        </Typography>
                      </Box>
                    )}
                  </label>
                </Box>
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: { xs: "stretch", sm: "flex-end" },
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    disabled={loading}
                    fullWidth={isMobile}
                    sx={{ flexGrow: { xs: 1, sm: 0 } }}
                  >
                    Reset
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<SendIcon />}
                    disabled={loading}
                    fullWidth={isMobile}
                    sx={{ flexGrow: { xs: 1, sm: 0 } }}
                  >
                    {loading ? "Sending..." : "Send Notification"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Push Notification Table */}
      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              mb: 3,
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 0 },
            }}
          >
            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              sx={{ fontWeight: 600 }}
            >
              Recent Notifications ({notifications.length})
            </Typography>
            <TextField
              size="small"
              placeholder="Search by Title"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
              sx={{ width: { xs: "100%", sm: 250 } }}
            />
          </Box>

          {/* Mobile View - Accordion Layout */}
          {isMobile ? (
            <Box>
              {filteredNotifications.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    py: 8,
                    textAlign: "center",
                  }}
                >
                  <FolderIcon sx={{ fontSize: 60, color: "#ccc", mb: 2 }} />
                  <Typography variant="h6" sx={{ color: "#666", mb: 1 }}>
                    No notifications found
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#999" }}>
                    Create your first notification using the form above
                  </Typography>
                </Box>
              ) : (
                filteredNotifications.map((notification, index) => (
                  <Accordion key={notification._id} sx={{ mb: 1 }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: notification.read
                          ? "transparent"
                          : "#f8f9fa",
                        "&:hover": { backgroundColor: "#f5f5f5" },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          width: "100%",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, minWidth: 30 }}
                        >
                          {index + 1}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {notification.title}
                        </Typography>
                        <Chip
                          label={notification.type || "promotion"}
                          size="small"
                          color="primary"
                          sx={{ fontWeight: 500, fontSize: "0.7rem" }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: "#666", fontWeight: 600 }}
                          >
                            Message:
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {notification.message}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <Chip
                              label="Sent"
                              size="small"
                              color="success"
                              sx={{ fontWeight: 500 }}
                            />
                            <Typography
                              variant="caption"
                              sx={{ color: "#666" }}
                            >
                              To: {notification.sentTo || "All Users"}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <IconButton size="small" color="info">
                              <ViewIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(notification._id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </Box>
          ) : (
            /* Desktop View - Table Layout */
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                overflowX: "auto",
                "& .MuiTableCell-root": {
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  padding: { xs: "8px", sm: "12px" },
                },
              }}
            >
              <Table>
                <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                  <TableRow>
                    <TableCell
                      sx={{ display: { xs: "none", sm: "table-cell" } }}
                    >
                      SL
                    </TableCell>
                    <TableCell sx={{ minWidth: { xs: 100, sm: 120 } }}>
                      Title
                    </TableCell>
                    <TableCell
                      sx={{
                        minWidth: { xs: 120, sm: 150 },
                        display: { xs: "none", sm: "table-cell" },
                      }}
                    >
                      Message
                    </TableCell>
                    <TableCell sx={{ minWidth: 80 }}>Type</TableCell>
                    <TableCell
                      sx={{
                        minWidth: 80,
                        display: { xs: "none", sm: "table-cell" },
                      }}
                    >
                      Sent To
                    </TableCell>
                    <TableCell sx={{ minWidth: 60 }}>Status</TableCell>
                    <TableCell sx={{ minWidth: 80 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredNotifications.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={isMobile ? 4 : 7}
                        sx={{ textAlign: "center", py: 8 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <FolderIcon
                            sx={{ fontSize: 60, color: "#ccc", mb: 2 }}
                          />
                          <Typography
                            variant="h6"
                            sx={{ color: "#666", mb: 1 }}
                          >
                            No notifications found
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#999" }}>
                            Create your first notification using the form above
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredNotifications.map((notification, index) => (
                      <TableRow key={notification._id} hover>
                        <TableCell
                          sx={{ display: { xs: "none", sm: "table-cell" } }}
                        >
                          {index + 1}
                        </TableCell>
                        <TableCell sx={{ minWidth: { xs: 100, sm: 120 } }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {notification.title}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            minWidth: { xs: 120, sm: 150 },
                            display: { xs: "none", sm: "table-cell" },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: { xs: 120, sm: 200 },
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {notification.message}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 80 }}>
                          <Chip
                            label={notification.type || "promotion"}
                            size="small"
                            color="primary"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            minWidth: 80,
                            display: { xs: "none", sm: "table-cell" },
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {notification.sentTo || "All Users"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 60 }}>
                          <Chip
                            label="Sent"
                            size="small"
                            color="success"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 80 }}>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <IconButton size="small" color="info">
                              <ViewIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(notification._id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SendNotification;
